import { useEffect, useRef, useState } from "react";

/**
 * Intersection-observer hook.
 * - Default: TOGGLES `inView` true/false whenever the element enters/leaves
 *   the viewport. This means components animate IN on entry and OUT on exit,
 *   in both scroll directions (down AND up).
 * - Pass `once: true` to keep the original "trigger once and disconnect" behaviour.
 */
export function useReveal({
    threshold = 0.18,
    rootMargin = "0px 0px -10% 0px",
    once = false,
} = {}) {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);

    useEffect(() => {
        if (!ref.current) return;
        const node = ref.current;
        const obs = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setInView(true);
                    if (once) obs.disconnect();
                } else if (!once) {
                    setInView(false);
                }
            },
            { threshold, rootMargin }
        );
        obs.observe(node);
        return () => obs.disconnect();
    }, [threshold, rootMargin, once]);

    return [ref, inView];
}
